import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Container, Typography, Grid, Card, CardContent,
    Skeleton, Stack, Chip, Avatar, Divider, Button,
    LinearProgress, Tooltip
} from '@mui/material';
import TrendingUpIcon         from '@mui/icons-material/TrendingUp';
import TrendingDownIcon       from '@mui/icons-material/TrendingDown';
import PeopleIcon             from '@mui/icons-material/People';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PendingActionsIcon     from '@mui/icons-material/PendingActions';
import AttachMoneyIcon        from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon      from '@mui/icons-material/CalendarToday';
import DirectionsBikeIcon     from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon      from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon     from '@mui/icons-material/DirectionsWalk';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import HourglassEmptyIcon     from '@mui/icons-material/HourglassEmpty';
import RefreshIcon            from '@mui/icons-material/Refresh';
import EmojiEventsIcon       from '@mui/icons-material/EmojiEvents';
import ScheduleIcon          from '@mui/icons-material/Schedule';
import axiosInstance from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';
import { useCurrency }    from '../context/CurrencyContext';
import { formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';

// ── Helpers ───────────────────────────────────────────────────────────────────
function getUtcWeekBounds() {
    const now = new Date();
    const day = now.getUTCDay();
    const diffToMon = (day === 0 ? -6 : 1 - day);
    const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diffToMon));
    const sunday = new Date(monday.getTime() + 6 * 86_400_000 + 86_399_999);
    return { monday, sunday };
}
function getUtcDayKey(isoString) { return isoString ? isoString.substring(0, 10) : null; }
function getLast7Days() {
    const days = [], now = new Date();
    for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
        days.push(d.toISOString().substring(0, 10));
    }
    return days;
}
function formatDayLabel(isoDay) {
    return new Date(isoDay + 'T00:00:00Z')
        .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCardSkeleton() {
    return (
        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Skeleton variant="text"     width={120} height={18} />
                <Skeleton variant="circular" width={40}  height={40} />
            </Box>
            <Skeleton variant="text" width={80}  height={42} />
            <Skeleton variant="text" width={100} height={16} sx={{ mt: 0.5 }} />
        </Card>
    );
}

function StatCard({ label, value, sub, icon, iconBg, trend, trendLabel }) {
    return (
        <Card elevation={0} sx={{
            border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3,
            background: `linear-gradient(135deg, #FFFFFF 0%, ${PARCHMENT} 100%)`,
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: '0 8px 28px rgba(196,98,45,0.10)' },
        }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {label}
                    </Typography>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: iconBg || STONE,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {icon}
                    </Box>
                </Box>
                <Typography variant="h4" fontWeight={900} sx={{ color: WALNUT, lineHeight: 1 }}>{value}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8 }}>
                    {trend !== undefined && (trend >= 0
                            ? <TrendingUpIcon   sx={{ fontSize: '0.9rem', color: FOREST }} />
                            : <TrendingDownIcon sx={{ fontSize: '0.9rem', color: '#C0392B' }} />
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
                    {trendLabel && (
                        <Typography variant="caption" sx={{ fontWeight: 700, color: trend >= 0 ? FOREST : '#C0392B' }}>
                            {trendLabel}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}

function RevenueBar({ days, dataByDay, currency }) {
    const maxVal = Math.max(...days.map(d => dataByDay[d] || 0), 1);
    const today  = new Date().toISOString().substring(0, 10);
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: 80, mt: 1 }}>
            {days.map(day => {
                const val = dataByDay[day] || 0;
                const pct = (val / maxVal) * 100;
                const isToday = day === today;
                return (
                    <Tooltip key={day} title={`${formatDayLabel(day)}: ${formatCurrency(val, currency)}`} arrow>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: '4px', cursor: 'default' }}>
                            <Box sx={{ width: '100%', height: `${Math.max(pct, 4)}%`, minHeight: 4,
                                borderRadius: '4px 4px 0 0',
                                bgcolor: isToday ? TERRACOTTA : 'rgba(196,98,45,0.35)',
                                transition: 'height 0.4s ease' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem',
                                color: isToday ? TERRACOTTA : 'text.secondary',
                                fontWeight: isToday ? 700 : 400 }}>
                                {formatDayLabel(day).split(' ')[0]}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ManagerDashboard() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t }    = useTranslation();
    const { currency, convert, format } = useCurrency();

    const [bills,       setBills]       = useState([]);
    const [tourOffers,  setTourOffers]  = useState([]);
    const [tours,       setTours]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState('');
    const [lastRefresh, setLastRefresh] = useState(null);

    useEffect(() => {
        if (!authLoading && user?.role !== 'MANAGER' && user?.role !== 'ADMIN') navigate('/', { replace: true });
    }, [user, authLoading, navigate]);

    const fetchData = async () => {
        setLoading(true); setError('');
        try {
            const [billsRes, toursOfferRes, toursRes] = await Promise.all([
                axiosInstance.get('/bills/view?size=1000'),
                axiosInstance.get('/api/tourOffers/view?size=1000'),
                axiosInstance.get('/api/tours/view?size=1000'),
            ]);
            setBills(billsRes.data?.content           || billsRes.data           || []);
            setTourOffers(toursOfferRes.data?.content || toursOfferRes.data      || []);
            setTours(toursRes.data?.content           || toursRes.data           || []);
            setLastRefresh(new Date());
        } catch {
            setError(t('dashboard.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []); // eslint-disable-line

    const stats = useMemo(() => {
        if (!bills.length) return null;
        const { monday, sunday } = getUtcWeekBounds();
        const last7  = getLast7Days();
        const isPaid   = b => b.billStatus === 'PAID' || b.billStatus === 'REFUND_REQUESTED';
        const isDraft  = b => b.billStatus === 'DRAFT';
        const isRefReq = b => b.billStatus === 'REFUND_REQUESTED';
        const inWeek   = b => { if (!b.created_at) return false; const d = new Date(b.created_at); return d >= monday && d <= sunday; };
        const inLast7  = b => { if (!b.created_at) return false; return last7.includes(getUtcDayKey(b.created_at)); };

        const totalRevenue   = bills.filter(isPaid).reduce((s, b) => s + convert(b.totalAmount || 0, b.currency || 'EUR'), 0);
        const weekRevenue    = bills.filter(b => isPaid(b) && inWeek(b)).reduce((s, b) => s + convert(b.totalAmount || 0, b.currency || 'EUR'), 0);
        const pendingRevenue = bills.filter(isDraft).reduce((s, b) => s + convert(b.totalAmount || 0, b.currency || 'EUR'), 0);
        const totalBookings  = bills.length;
        const weekBookings   = bills.filter(inWeek).length;
        const paidCount      = bills.filter(isPaid).length;
        const draftCount     = bills.filter(isDraft).length;
        const canceledCount  = bills.filter(b => b.billStatus === 'CANCELED').length;
        const refundedCount  = bills.filter(b => b.billStatus === 'REFUNDED').length;
        const refundReqCount = bills.filter(isRefReq).length;
        const uniqueCustomers = new Set(bills.map(b => b.customerId).filter(Boolean)).size;

        const revenueByDay = {};
        last7.forEach(d => { revenueByDay[d] = 0; });
        bills.filter(b => isPaid(b) && inLast7(b)).forEach(b => {
            const key = getUtcDayKey(b.created_at);
            if (key) revenueByDay[key] = (revenueByDay[key] || 0) + convert(b.totalAmount || 0, b.currency || 'EUR');
        });

        const tourBookingCount = {}, tourRevenue = {};
        bills.forEach(b => { if (b.sourceId) tourBookingCount[b.sourceId] = (tourBookingCount[b.sourceId] || 0) + 1; });
        bills.filter(isPaid).forEach(b => { if (b.sourceId) tourRevenue[b.sourceId] = (tourRevenue[b.sourceId] || 0) + convert(b.totalAmount || 0, b.currency || 'EUR'); });

        const topTours = Object.entries(tourBookingCount)
            .sort((a, b) => b[1] - a[1]).slice(0, 5)
            .map(([id, count]) => {
                const offer = tourOffers.find(t => t.id === id);
                return { id, title: offer?.title || t('dashboard.unknown_tour'), type: offer?.type || null, count, revenue: tourRevenue[id] || 0 };
            });

        const recentBills = [...bills]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);

        // Top guides — count tours per guide, top 5
        const guideCountMap = {}, guideNameMap = {};
        tours.forEach(tour => {
            if (tour.guideId) {
                guideCountMap[tour.guideId] = (guideCountMap[tour.guideId] || 0) + 1;
                if (tour.guideName) guideNameMap[tour.guideId] = tour.guideName;
            }
        });
        const topGuides = Object.entries(guideCountMap)
            .sort((a, b) => b[1] - a[1]).slice(0, 5)
            .map(([id, count]) => ({ id, name: guideNameMap[id] || '—', count }));

        // Upcoming tours this week (SCHEDULED, within Mon–Sun UTC)
        const upcomingTours = tours
            .filter(tour => tour.status === 'SCHEDULED' && tour.time)
            .filter(tour => { const d = new Date(tour.time); return d >= monday && d <= sunday; })
            .sort((a, b) => new Date(a.time) - new Date(b.time))
            .slice(0, 5);

        return { totalRevenue, weekRevenue, pendingRevenue, totalBookings, weekBookings, uniqueCustomers,
            paidCount, draftCount, canceledCount, refundedCount, refundReqCount,
            revenueByDay, topTours, topGuides, upcomingTours, recentBills, last7 };
    }, [bills, tourOffers, tours, convert]); // eslint-disable-line

    if (authLoading || (!authLoading && user?.role !== 'MANAGER' && user?.role !== 'ADMIN')) return null;

    // Status chip uses t() — defined inside render so language changes propagate
    const statusChip = (status) => {
        const map = {
            PAID:             { label: t('dashboard.status_confirmed'),     color: 'success' },
            DRAFT:            { label: t('dashboard.status_pending'),       color: 'warning' },
            CANCELED:         { label: t('dashboard.status_canceled'),      color: 'error'   },
            REFUNDED:         { label: t('dashboard.status_refunded'),      color: 'info'    },
            REFUND_REQUESTED: { label: t('dashboard.status_refund_pending'), color: 'warning' },
            DELETED:          { label: t('booking.status_hidden'),          color: 'default' },
        };
        const cfg = map[status] || { label: status, color: 'default' };
        return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 700, fontSize: '0.68rem' }} />;
    };

    const tourIcon = (type) => {
        const sx = { fontSize: '1rem' };
        if (type === 'BIKE') return <DirectionsBikeIcon sx={sx} />;
        if (type === 'BUS')  return <DirectionsBusIcon  sx={sx} />;
        if (type === 'WALK') return <DirectionsWalkIcon sx={sx} />;
        return null;
    };

    // Status breakdown rows built with t() so they translate
    const breakdownRows = stats ? [
        { label: t('dashboard.status_confirmed'),     count: stats.paidCount,      color: FOREST,     icon: <CheckCircleIcon    sx={{ fontSize: '0.9rem' }} /> },
        { label: t('dashboard.status_pending'),       count: stats.draftCount,     color: GOLD,       icon: <HourglassEmptyIcon sx={{ fontSize: '0.9rem' }} /> },
        { label: t('dashboard.status_refund_pending'), count: stats.refundReqCount, color: '#E67E22',  icon: <PendingActionsIcon sx={{ fontSize: '0.9rem' }} /> },
        { label: t('dashboard.status_canceled'),      count: stats.canceledCount,  color: '#C0392B',  icon: <CancelIcon         sx={{ fontSize: '0.9rem' }} /> },
        { label: t('dashboard.status_refunded'),      count: stats.refundedCount,  color: '#5B8DB8',  icon: <RefreshIcon        sx={{ fontSize: '0.9rem' }} /> },
    ] : [];

    const quickActions = [
        { label: t('dashboard.action_refunds'),  count: loading ? null : stats?.refundReqCount, color: '#C0392B',  path: '/my-bookings', urgent: true },
        { label: t('dashboard.action_bookings'), count: loading ? null : stats?.totalBookings,   color: TERRACOTTA, path: '/my-bookings' },
        { label: t('dashboard.action_tours'),    count: loading ? null : tourOffers.length,      color: FOREST,     path: '/' },
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://explorearmenia.am/images/2025/09/20/ea-background-min.webp") center/cover fixed` }}>

            {/* Header */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 55%, ${TERRACOTTA} 100%)`,
                pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 8 }, px: 2, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: 40, bgcolor: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }}>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.2em', fontWeight: 700 }}>
                                {t('dashboard.overline')}
                            </Typography>
                            <Typography variant="h3" fontWeight={900}
                                        sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", lineHeight: 1.1,
                                              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
                                {t('dashboard.title')}
                            </Typography>
                            {lastRefresh && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, display: 'block' }}>
                                    {t('dashboard.last_updated', {
                                        time: lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                    })}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
                                    onClick={fetchData} disabled={loading}
                                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', borderRadius: 2,
                                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                                {t('dashboard.refresh')}
                            </Button>
                            <Button variant="contained" size="small" startIcon={<ConfirmationNumberIcon />}
                                    component="a" href="/my-bookings"
                                    sx={{ bgcolor: TERRACOTTA, borderRadius: 2, '&:hover': { bgcolor: '#9A4A1F' } }}>
                                {t('dashboard.all_bookings')}
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mt: -2, pb: 8 }}>

                {/* Error */}
                {error && (
                    <Box sx={{ mb: 4, p: 3, bgcolor: '#FDEDED', borderRadius: 2,
                        border: '1px solid rgba(192,57,43,0.25)' }}>
                        <Typography color="error" variant="body2" fontWeight={600}>{error}</Typography>
                        <Button size="small" onClick={fetchData} sx={{ mt: 1, color: '#C0392B' }}>
                            {t('dashboard.try_again')}
                        </Button>
                    </Box>
                )}

                {/* KPI row */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {[0,1,2,3].map(i => (
                        <Grid item xs={12} sm={6} lg={3} key={i}>
                            {loading ? <StatCardSkeleton /> : stats && [
                                <StatCard
                                    label={t('dashboard.total_revenue')}
                                    value={formatCurrency(stats.totalRevenue, currency)}
                                    sub={t('dashboard.revenue_from')}
                                    icon={<AttachMoneyIcon sx={{ color: TERRACOTTA, fontSize: '1.3rem' }} />}
                                    iconBg="rgba(196,98,45,0.1)"
                                />,
                                <StatCard
                                    label={t('dashboard.this_week')}
                                    value={formatCurrency(stats.weekRevenue, currency)}
                                    sub={t(stats.weekBookings === 1 ? 'dashboard.week_bookings_one' : 'dashboard.week_bookings_other', { count: stats.weekBookings })}
                                    icon={<CalendarTodayIcon sx={{ color: FOREST, fontSize: '1.3rem' }} />}
                                    iconBg="rgba(74,103,65,0.1)"
                                />,
                                <StatCard
                                    label={t('dashboard.total_bookings')}
                                    value={stats.totalBookings}
                                    sub={t(stats.uniqueCustomers === 1 ? 'dashboard.customers_one' : 'dashboard.customers_other', { count: stats.uniqueCustomers })}
                                    icon={<PeopleIcon sx={{ color: GOLD, fontSize: '1.3rem' }} />}
                                    iconBg="rgba(201,151,58,0.1)"
                                />,
                                <StatCard
                                    label={t('dashboard.pending_refunds')}
                                    value={stats.refundReqCount}
                                    sub={stats.refundReqCount === 0 ? t('dashboard.nothing_pending') : t('dashboard.require_attention')}
                                    icon={<PendingActionsIcon sx={{ color: stats.refundReqCount > 0 ? '#C0392B' : FOREST, fontSize: '1.3rem' }} />}
                                    iconBg={stats.refundReqCount > 0 ? 'rgba(192,57,43,0.1)' : 'rgba(74,103,65,0.1)'}
                                />,
                            ][i]}
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3}>
                    {/* Left column */}
                    <Grid item xs={12} lg={6}>

                        {/* Revenue trend */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                            {t('dashboard.revenue_7days')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('dashboard.confirmed_utc')}
                                        </Typography>
                                    </Box>
                                    {!loading && stats && (
                                        <Typography variant="h6" fontWeight={900} sx={{ color: TERRACOTTA }}>
                                            {formatCurrency(Object.values(stats.revenueByDay).reduce((a, b) => a + b, 0), currency)}
                                        </Typography>
                                    )}
                                </Box>
                                {loading
                                    ? <Skeleton variant="rounded" width="100%" height={96} sx={{ borderRadius: 2 }} />
                                    : stats && <RevenueBar days={stats.last7} dataByDay={stats.revenueByDay} currency={currency} />
                                }
                            </CardContent>
                        </Card>

                        {/* Status breakdown */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mb: 2.5 }}>
                                    {t('dashboard.status_breakdown')}
                                </Typography>
                                {loading ? (
                                    <Stack spacing={2}>
                                        {[1,2,3,4].map(i => (
                                            <Box key={i}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Skeleton variant="text" width={80} height={16} />
                                                    <Skeleton variant="text" width={40} height={16} />
                                                </Box>
                                                <Skeleton variant="rounded" width="100%" height={8} sx={{ borderRadius: 4 }} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : stats && (
                                    <Stack spacing={2.5}>
                                        {breakdownRows.map(row => (
                                            <Box key={row.label}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', mb: 0.6 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: row.color }}>
                                                        {row.icon}
                                                        <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                                            {row.label}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: row.color }}>
                                                            {row.count}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({((row.count / (stats.totalBookings || 1)) * 100).toFixed(0)}%)
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <LinearProgress variant="determinate"
                                                                value={(row.count / (stats.totalBookings || 1)) * 100}
                                                                sx={{ height: 7, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.06)',
                                                                    '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 } }} />
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* Upcoming tours this week */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                                    <ScheduleIcon sx={{ color: FOREST, fontSize: '1.15rem' }} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                        {t('dashboard.upcoming_tours_week', 'Upcoming This Week')}
                                    </Typography>
                                </Box>
                                {loading ? (
                                    <Stack spacing={2}>
                                        {[1,2,3].map(i => (
                                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="60%" height={18} />
                                                    <Skeleton variant="text" width="40%" height={14} />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : !stats?.upcomingTours.length ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('dashboard.no_upcoming_tours', 'No scheduled tours this week.')}
                                    </Typography>
                                ) : stats.upcomingTours.map((tour, i) => (
                                    <React.Fragment key={tour.id}>
                                        {i > 0 && <Divider sx={{ my: 1.5, borderColor: 'rgba(196,98,45,0.08)' }} />}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                                                bgcolor: 'rgba(74,103,65,0.08)', display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', flexDirection: 'column' }}>
                                                <Typography variant="caption" fontWeight={900} sx={{ color: FOREST, lineHeight: 1, fontSize: '0.85rem' }}>
                                                    {new Date(tour.time).toLocaleDateString('en-GB', { day: 'numeric', timeZone: 'UTC' })}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                                                    {new Date(tour.time).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {tourIcon(tour.type) && (
                                                        <Box sx={{ color: TERRACOTTA, display: 'flex', flexShrink: 0 }}>
                                                            {tourIcon(tour.type)}
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: WALNUT }}>
                                                        {tour.offerTitle || t('dashboard.unknown_tour')}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {tour.guideName
                                                        ? `${t('dashboard.guide_prefix', 'Guide')}: ${tour.guideName}`
                                                        : t('dashboard.no_guide', 'No guide assigned')}
                                                    {' · '}{new Date(tour.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Recent bookings */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                        {t('dashboard.recent_bookings')}
                                    </Typography>
                                    <Button size="small" variant="outlined" component="a" href="/my-bookings"
                                            sx={{ color: TERRACOTTA, borderColor: 'rgba(196,98,45,0.35)',
                                                borderRadius: 2, fontSize: '0.75rem',
                                                '&:hover': { borderColor: TERRACOTTA, bgcolor: 'rgba(196,98,45,0.04)' } }}>
                                        {t('dashboard.view_all')}
                                    </Button>
                                </Box>
                                {loading ? (
                                    <Stack spacing={2}>
                                        {[1,2,3,4,5].map(i => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Skeleton variant="circular" width={36} height={36} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="55%" height={18} />
                                                    <Skeleton variant="text" width="35%" height={14} />
                                                </Box>
                                                <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '20px' }} />
                                                <Skeleton variant="text"    width={50} height={20} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : stats?.recentBills.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">{t('dashboard.no_booking_data')}</Typography>
                                ) : stats?.recentBills.map((bill, i) => {
                                    const offer = tourOffers.find(to => to.id === bill.sourceId);
                                    return (
                                        <React.Fragment key={bill.id}>
                                            {i > 0 && <Divider sx={{ my: 1.5, borderColor: 'rgba(196,98,45,0.08)' }} />}
                                            <Box component="a" href={`/booking/${bill.id}`}
                                                 sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
                                                borderRadius: 2, p: 0.5, transition: '0.2s', textDecoration: 'none', color: 'inherit',
                                                '&:hover': { bgcolor: 'rgba(196,98,45,0.04)' } }}>
                                                <Avatar src={bill.customerImageUrl} alt={bill.customerName}
                                                        sx={{ width: 36, height: 36, bgcolor: TERRACOTTA, fontSize: '0.85rem' }}>
                                                    {(bill.customerName || '?')[0].toUpperCase()}
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: WALNUT }}>
                                                        {bill.customerName || t('dashboard.unknown_customer')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {offer?.title || t('dashboard.unknown_tour')} ·{' '}
                                                        {bill.created_at ? new Date(bill.created_at).toLocaleDateString('en-GB',
                                                            { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '—'}
                                                    </Typography>
                                                </Box>
                                                {statusChip(bill.billStatus)}
                                                <Typography variant="body2" fontWeight={700}
                                                            sx={{ color: TERRACOTTA, flexShrink: 0 }}>
                                                    {format(bill.totalAmount || 0, bill.currency || 'EUR')}
                                                </Typography>
                                            </Box>
                                        </React.Fragment>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right column */}
                    <Grid item xs={12} lg={6}>

                        {/* Pending revenue */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.20)', borderRadius: 3, mb: 3,
                            background: `linear-gradient(135deg, ${WALNUT} 0%, #6B3A20 100%)`, color: 'white' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    {t('dashboard.pending_revenue')}
                                </Typography>
                                {loading
                                    ? <Skeleton variant="text" width={120} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                                    : <Typography variant="h4" fontWeight={900} sx={{ color: GOLD, mt: 0.5 }}>
                                        {formatCurrency(stats?.pendingRevenue || 0, currency)}
                                    </Typography>
                                }
                                <Typography variant="caption" sx={{ opacity: 0.55 }}>
                                    {loading ? '—' : t(stats?.draftCount === 1 ? 'dashboard.unpaid_one' : 'dashboard.unpaid_other',
                                        { count: stats?.draftCount || 0 })}
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Top tours */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mb: 2.5 }}>
                                    {t('dashboard.top_tours')}
                                </Typography>
                                {loading ? (
                                    <Stack spacing={2}>
                                        {[1,2,3].map(i => (
                                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                <Skeleton variant="circular" width={28} height={28} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="75%" height={18} />
                                                    <Skeleton variant="text" width="40%" height={14} />
                                                </Box>
                                                <Skeleton variant="text" width={30} height={24} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : stats?.topTours.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">{t('dashboard.no_booking_data')}</Typography>
                                ) : stats?.topTours.map((tour, i) => (
                                    <React.Fragment key={tour.id}>
                                        {i > 0 && <Divider sx={{ my: 1.5, borderColor: 'rgba(196,98,45,0.08)' }} />}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: i === 0 ? GOLD : i === 1 ? STONE : 'rgba(196,98,45,0.08)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography variant="caption" fontWeight={900}
                                                            sx={{ color: i === 0 ? WALNUT : 'text.secondary' }}>
                                                    {i + 1}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {tourIcon(tour.type) && (
                                                        <Box sx={{ color: TERRACOTTA, display: 'flex', flexShrink: 0 }}>
                                                            {tourIcon(tour.type)}
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: WALNUT }}>
                                                        {tour.title}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatCurrency(tour.revenue, currency)} {t('dashboard.revenue_suffix')}
                                                </Typography>
                                            </Box>
                                            <Chip label={`${tour.count} ${t('dashboard.bookings_suffix')}`} size="small"
                                                  sx={{ bgcolor: 'rgba(196,98,45,0.08)', color: TERRACOTTA,
                                                      fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }} />
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Top guides */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                                    <EmojiEventsIcon sx={{ color: GOLD, fontSize: '1.15rem' }} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                        {t('dashboard.top_guides', 'Top Guides')}
                                    </Typography>
                                </Box>
                                {loading ? (
                                    <Stack spacing={2}>
                                        {[1,2,3].map(i => (
                                            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                <Skeleton variant="circular" width={28} height={28} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" height={18} />
                                                </Box>
                                                <Skeleton variant="text" width={30} height={24} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : !stats?.topGuides.length ? (
                                    <Typography variant="body2" color="text.secondary">{t('dashboard.no_guide_data', 'No tour assignments yet.')}</Typography>
                                ) : stats.topGuides.map((guide, i) => (
                                    <React.Fragment key={guide.id}>
                                        {i > 0 && <Divider sx={{ my: 1.5, borderColor: 'rgba(196,98,45,0.08)' }} />}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: i === 0 ? GOLD : i === 1 ? STONE : 'rgba(196,98,45,0.08)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography variant="caption" fontWeight={900}
                                                    sx={{ color: i === 0 ? WALNUT : 'text.secondary' }}>
                                                    {i + 1}
                                                </Typography>
                                            </Box>
                                            <Avatar sx={{ width: 26, height: 26, bgcolor: TERRACOTTA, fontSize: '0.7rem', fontWeight: 700 }}>
                                                {(guide.name || '?')[0].toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: WALNUT, flex: 1 }}>
                                                {guide.name}
                                            </Typography>
                                            <Chip label={`${guide.count} ${t('dashboard.tours_suffix', 'tours')}`} size="small"
                                                sx={{ bgcolor: 'rgba(196,98,45,0.08)', color: TERRACOTTA,
                                                    fontWeight: 700, fontSize: '0.7rem', flexShrink: 0 }} />
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Quick actions */}
                        <Card elevation={0} sx={{ border: '1px solid rgba(196,98,45,0.12)', borderRadius: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT, mb: 2 }}>
                                    {t('dashboard.quick_actions')}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {quickActions.map(action => (
                                        <Button key={action.label} fullWidth variant="outlined"
                                                component="a" href={action.path}
                                                sx={{ justifyContent: 'space-between', borderRadius: 2,
                                                    color: action.color,
                                                    borderColor: action.urgent && (stats?.refundReqCount || 0) > 0
                                                        ? action.color : 'rgba(196,98,45,0.25)',
                                                    bgcolor: action.urgent && (stats?.refundReqCount || 0) > 0
                                                        ? 'rgba(192,57,43,0.04)' : 'transparent',
                                                    '&:hover': { borderColor: action.color, bgcolor: `${action.color}10` },
                                                    fontWeight: 600, fontSize: '0.82rem',
                                                    textTransform: 'none', px: 2, py: 1 }}>
                                            <span>{action.label}</span>
                                            {action.count !== null && (
                                                <Chip label={action.count} size="small"
                                                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700,
                                                          bgcolor: `${action.color}18`, color: action.color,
                                                          pointerEvents: 'none' }} />
                                            )}
                                        </Button>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}